import type {
    ISourceDomain,
    ISourceTopic,
    ISourceDomainResponse,
    IDomainDailyData,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/types";
import { formatTopicToId } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sources-report/domain-influence/utils";

import { assignDynamicLegendColors } from "../../../../shared/LLMTrackerUtils";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";

import { MAX_DOMAINS } from "../consts";

import { MAX_SELECTED_DOMAINS } from "./consts";
import type { SeriesLineData, SeriesLineItem } from "./types";

export const transformData = (data: ISourceDomainResponse): SeriesLineData => {
    const allTopicsItem: ISourceTopic = {
        ...data.aggregated,
        topicName: ALL_TOPICS_NAME,
    };

    const topics = [allTopicsItem, ...(data.topics ?? [])];

    return topics.reduce<SeriesLineData>((acc, { topicName, domains }) => {
        const topicKey = formatTopicToId(topicName);
        const topDomains = domains
            .filter((domain) => domain.daily && Object.keys(domain.daily).length)
            .sort((a, b) => b.sourceAuthority - a.sourceAuthority)
            .slice(0, MAX_DOMAINS);

        const domainsWithData = topDomains.map((domain: ISourceDomain, index: number) => {
            const dailyMap: Record<
                string,
                Pick<IDomainDailyData, "influenceScore" | "dailyUrlCount">
            > = Object.fromEntries(
                (domain.daily ?? []).map((d) => [
                    d.date,
                    {
                        influenceScore: d.influenceScore,
                        dailyUrlCount: d.dailyUrlCount,
                    },
                ]),
            );

            const result: SeriesLineItem = {
                id: `${topicKey}-${domain.domain}`,
                name: domain.domain ?? "",
                total: domain.sourceAuthority,
                visible: index < MAX_SELECTED_DOMAINS,
                color: "",
                urlData: Object.keys(dailyMap)
                    .sort()
                    .map((date: string) => [new Date(date).getTime(), dailyMap[date].dailyUrlCount]),
                data: Object.keys(dailyMap)
                    .sort()
                    .map((date: string) => [new Date(date).getTime(), dailyMap[date].influenceScore]),
            };

            return result;
        });

        acc[topicKey] = assignDynamicLegendColors(domainsWithData);
        return acc;
    }, {});
};
